import { api, apiBlobRequest } from '@/lib/api-client'
import type {
  AdminTripEdit,
  PageResponse,
  TripEditDecisionRequest,
  TripEditFilters,
} from '@/types/api'

/**
 * The review queue.
 *
 * The default filter is `OPEN`, which the server expands into the three states
 * still waiting on somebody. Deliberately not expressed here: a state added
 * later is then picked up without a dashboard release.
 */
export function fetchTripEdits(filters: TripEditFilters): Promise<PageResponse<AdminTripEdit>> {
  return api.get<PageResponse<AdminTripEdit>>('/api/admin/trip-edits', { query: { ...filters } })
}

export function fetchTripEdit(id: number): Promise<AdminTripEdit> {
  return api.get<AdminTripEdit>(`/api/admin/trip-edits/${id}`)
}

/** How many changes are waiting on somebody. Drives the sidebar count. */
export function fetchOpenTripEditCount(): Promise<{ count: number }> {
  return api.get<{ count: number }>('/api/admin/trip-edits/open-count')
}

/** Every change ever asked for against one booking, for the booking drawer. */
export function fetchTripEditsForBooking(tripRequestId: number): Promise<AdminTripEdit[]> {
  return api.get<AdminTripEdit[]>(`/api/admin/trip-edits/for-booking/${tripRequestId}`)
}

/**
 * Approves or rejects a change, and prices it.
 *
 * Approving with no `extraAmount` applies the change immediately. Approving
 * with one changes nothing on the booking — the customer is asked to pay first,
 * and only the payment moves it. That asymmetry is the whole feature, so it is
 * the server's rule rather than something this screen arranges.
 */
export function decideTripEdit(
  editId: number,
  decision: TripEditDecisionRequest,
): Promise<AdminTripEdit> {
  return api.patch<AdminTripEdit>(`/api/admin/trip-edits/${editId}/decision`, decision)
}

/**
 * Confirms — or refuses — the Instapay transfer for an approved change.
 *
 * Accepting is the moment an Instapay change becomes real. Refusing puts it
 * back to awaiting payment with the note attached, so the customer sends the
 * right receipt instead of starting the whole request again.
 */
export function verifyTripEditPayment(
  editId: number,
  accepted: boolean,
  note?: string,
): Promise<AdminTripEdit> {
  return api.patch<AdminTripEdit>(`/api/admin/trip-edits/${editId}/payment-verification`, undefined, {
    query: { accepted, note },
  })
}

/**
 * The transfer receipt for a change's extra charge.
 *
 * **The caller must revoke the returned object URL** once the image is no
 * longer shown — see {@link apiBlobRequest}.
 */
export function fetchTripEditProof(editId: number): Promise<{ objectUrl: string; contentType: string }> {
  return apiBlobRequest(`/api/admin/trip-edits/${editId}/payment-proof`)
}
